import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Target, Building2, FileText, CheckCircle2, Download, Loader2, Wallet, Package,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  fmtXof, fmtPct, fmtDate, initials, avatarColor, ROLE_LABEL, progressColor,
} from './perfMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, testid }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-gray-800 tabular-nums" data-testid={testid}>{value}</span>
  </div>
);

const ActionBtn = ({ icon: Icon, label, onClick, tone = 'default', disabled, testid }) => {
  const tones = { default: 'border-gray-200 text-gray-700 hover:bg-gray-50', green: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' };
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border p-3 text-[11px] font-medium transition-colors disabled:opacity-50 ${tones[tone]}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
};

export const CommercialDrawer = ({ commercial, period, canManage, onClose, onDefineTarget, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/commercials/${commercial.commercialId}/dashboard`, { params: { period } });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Détail indisponible'); onClose(); }
    finally { setLoading(false); }
  }, [commercial.commercialId, period, onClose]);

  useEffect(() => { load(); }, [load]);

  const validateCommissions = async () => {
    if (!window.confirm(`Valider (approuver) toutes les commissions en attente de ${commercial.commercialName} ?`)) return;
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/commissions/validate-batch`, null, { params: { period, commercial_id: commercial.commercialId } });
      toast.success(`${r.approved} commission(s) approuvée(s)`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la validation'); }
    finally { setBusy(false); }
  };

  const exportDetail = () => {
    if (!data) return;
    const lines = [`Commercial,${commercial.commercialName}`, `Periode,${period}`,
      `CA realise,${data.ca?.realised || 0}`, `Objectif,${data.ca?.target || 0}`,
      `Ops payees,${data.ops?.realised || 0}`, `Factures,${data.invoices?.realised || 0}`,
      `Commission a valider,${data.commissions?.pending || 0}`, `Commission versee,${data.commissions?.paid || 0}`];
    const url = window.URL.createObjectURL(new Blob([`\ufeff${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a'); a.href = url; a.download = `perf_${commercial.commercialName}_${period}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('Détail exporté');
  };

  const c = data?.commissions || {};
  const recent = data?.recentActivity || {};

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 xl:hidden" onClick={onClose} data-testid="perf-drawer-backdrop" />
      <aside className="fixed right-0 top-16 bottom-0 z-50 w-full overflow-y-auto border-l border-gray-200 bg-white shadow-xl sm:w-[440px] sm:top-20" data-testid="perf-commercial-drawer">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className={`flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold text-white ${avatarColor(commercial.commercialId)}`}>{initials(commercial.commercialName)}</span>
            <div>
              <div className="text-xs text-gray-500">Détail commercial</div>
              <div className="font-semibold text-gray-900">{commercial.commercialName}</div>
              <div className="text-[11px] text-gray-400">{ROLE_LABEL[commercial.role] || 'Commercial'}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="perf-drawer-close"><X className="h-5 w-5" /></button>
        </div>

        {loading || !data ? (
          <div className="space-y-3 p-5">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
        ) : (
          <div className="px-5 py-4">
            <h3 className="mb-1 text-sm font-semibold text-gray-900">Performance</h3>
            <div className="divide-y divide-gray-50">
              <Stat label="Portefeuille" value={`${data.portfolioSize ?? 0} clients`} />
              <Stat label="CA réalisé" value={fmtXof(data.ca?.realised)} testid="perf-drawer-ca" />
              <Stat label="Objectif mensuel" value={data.ca?.target ? fmtXof(data.ca.target) : 'Non défini'} />
              <Stat label="Progression" value={fmtPct(data.ca?.pct)} />
              <Stat label="Opérations payées" value={data.ops?.realised ?? 0} />
              <Stat label="Factures" value={data.invoices?.realised ?? 0} />
              <Stat label="Taux d'encaissement 30j" value={fmtPct(data.cashRecovery?.pct)} />
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div className={`h-full rounded-full ${progressColor(data.ca?.pct)}`} style={{ width: `${Math.min(100, data.ca?.pct || 0)}%` }} />
            </div>

            <h3 className="mb-1 mt-5 text-sm font-semibold text-gray-900">Commissions</h3>
            <div className="grid grid-cols-2 gap-2">
              {[['À valider', c.pending, 'text-orange-600'], ['Approuvées', c.approved, 'text-amber-600'], ['Versées', c.paid, 'text-emerald-600'], ['Reste à payer', c.remaining, 'text-red-600']].map(([l, v, cls]) => (
                <div key={l} className="rounded-lg border border-gray-100 p-2.5">
                  <div className="text-[11px] text-gray-500">{l}</div>
                  <div className={`text-sm font-bold tabular-nums ${cls}`}>{fmtXof(v || 0)}</div>
                </div>
              ))}
            </div>

            <h3 className="mb-2 mt-5 text-sm font-semibold text-gray-900">Activité récente</h3>
            {(recent.paidOps?.length || recent.invoices?.length || recent.commissions?.length) ? (
              <ul className="space-y-1.5">
                {(recent.paidOps || []).slice(0, 3).map((o, i) => (
                  <li key={`o${i}`} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-xs">
                    <span className="flex items-center gap-1.5 text-gray-700"><Package className="h-3 w-3 text-violet-500" /> {o.operationNumber} · {o.client}</span>
                    <span className="font-medium text-gray-600">{fmtXof(o.amount)}</span>
                  </li>
                ))}
                {(recent.invoices || []).slice(0, 3).map((iv, i) => (
                  <li key={`i${i}`} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-xs">
                    <span className="flex items-center gap-1.5 text-gray-700"><FileText className="h-3 w-3 text-blue-500" /> {iv.invoiceNumber}</span>
                    <span className="text-gray-400">{fmtDate(iv.date)}</span>
                  </li>
                ))}
                {(recent.commissions || []).slice(0, 3).map((cm, i) => (
                  <li key={`c${i}`} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-xs">
                    <span className="flex items-center gap-1.5 text-gray-700"><Wallet className="h-3 w-3 text-emerald-500" /> Commission {cm.status}</span>
                    <span className="font-medium text-gray-600">{fmtXof(cm.commissionAmount)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-lg bg-gray-50 px-3 py-4 text-center text-xs text-gray-400">Aucune activité récente sur cette période.</div>
            )}

            <h3 className="mb-2 mt-5 text-sm font-semibold text-gray-900">Actions rapides</h3>
            <div className="grid grid-cols-3 gap-2">
              {canManage && <ActionBtn icon={Target} label="Définir objectif" onClick={() => onDefineTarget(commercial)} testid="perf-action-target" />}
              <ActionBtn icon={Building2} label="Voir portefeuille" onClick={() => window.open('/admin/clients', '_blank')} testid="perf-action-portfolio" />
              <ActionBtn icon={FileText} label="Voir factures" onClick={() => window.open('/admin/operations', '_blank')} testid="perf-action-invoices" />
              {canManage && <ActionBtn icon={CheckCircle2} label="Valider commission" tone="green" onClick={validateCommissions} disabled={busy || !c.pending} testid="perf-action-validate" />}
              <ActionBtn icon={Download} label="Exporter le détail" onClick={exportDetail} testid="perf-action-export" />
            </div>
            {busy && <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Traitement…</div>}
          </div>
        )}
      </aside>
    </>
  );
};

export default CommercialDrawer;
